import React, { useState } from "react";
import { Card, Empty, ErrorState, Field, Kpi, PageHeading, Person, Pill, Rate, RowsSkeleton, SearchInput, Select, Table, Toggle, Workspace } from "../ui";
import { useFilters } from "../filters";
import { NAMES, personLabel, useView } from "../data";
import { ago, fmt, pct, statusOf, when } from "../format";
import { ACTIVE_SINCE, MIN_REQUESTS, SUCCESS_BANDS, Toolbar, activeWithin, countActive, inBand, matches } from "./lists";

const DEFAULTS = { q: "", who: "", only: {}, minRequests: "", success: "", activeSince: "" };

/** Everyone who used the agent in the period. A row opens their conversations. */
export default function People() {
  const { params, set, f: global } = useFilters();
  const { data, loading, error } = useView("users", params);
  const [f, setF] = useState(DEFAULTS);
  const on = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const only = (k) => (v) => setF((x) => ({ ...x, only: { ...x.only, [k]: v } }));
  const all = data?.users ?? [];
  const rows = all
    .filter((u) => !global.person || u.userId === global.person)
    .filter((u) => matches(f.q, personLabel(u.userId), NAMES.users.get(u.userId)?.email, u.userId))
    .filter((u) => !f.who || (f.who === "returning" ? u.returning : !u.returning))
    .filter((u) => !f.only.failed || u.failed > 0)
    .filter((u) => !f.only.friction || u.friction > 0)
    .filter((u) => !f.only.open || u.open > 0)
    .filter((u) => !f.only.rated || u.positive + u.negative > 0)
    .filter((u) => !f.only.negative || u.negative > 0)
    .filter((u) => !f.only.saved || u.recordsSaved > 0)
    .filter((u) => !f.minRequests || u.runs >= Number(f.minRequests))
    .filter((u) => inBand(u.successRate, f.success))
    .filter((u) => activeWithin(u.lastActive, f.activeSince));
  const newcomers = all.filter((u) => !u.returning).length;
  const pos = all.reduce((s, u) => s + u.positive, 0);
  const neg = all.reduce((s, u) => s + u.negative, 0);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="People">Everyone who used the agent in this period — new or returning, how it went for them, and how they rated it. Click someone to read their conversations.</PageHeading>

      <div className="aa-grid aa-grid-kpi">
        <Kpi loading={loading} label="Active people" value={fmt(all.length)} />
        <Kpi loading={loading} label="New in this period" value={fmt(newcomers)} foot={all.length ? `${pct(newcomers / all.length)} of active` : null} />
        <Kpi loading={loading} label="Returning" value={fmt(all.length - newcomers)} foot="used it before this period" />
        <Kpi loading={loading} label="Positive ratings" value={pos + neg ? pct(pos / (pos + neg)) : "—"} status={pos + neg ? statusOf(pos / (pos + neg), { good: 0.8, bad: 0.6 }) : "none"} foot={`${fmt(pos)} 👍 · ${fmt(neg)} 👎`} />
      </div>

      <Toolbar shown={rows.length} total={all.length} noun="people" active={countActive(f)} onClear={() => setF(DEFAULTS)}>
        <Field label="Search">
          <SearchInput value={f.q} onChange={on("q")} placeholder="Name, email or id" />
        </Field>
        <Field label="Who">
          <Select value={f.who} onChange={on("who")} options={[["", "Everyone"], ["new", "New"], ["returning", "Returning"]]} />
        </Field>
        <Field label="Requests">
          <Select value={f.minRequests} onChange={on("minRequests")} options={MIN_REQUESTS} />
        </Field>
        <Field label="Success">
          <Select value={f.success} onChange={on("success")} options={SUCCESS_BANDS} />
        </Field>
        <Field label="Last active">
          <Select value={f.activeSince} onChange={on("activeSince")} options={ACTIVE_SINCE} />
        </Field>
        <Field label="Only people who">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Toggle on={f.only.failed} onChange={only("failed")}>Had failures</Toggle>
            <Toggle on={f.only.friction} onChange={only("friction")}>Got stuck</Toggle>
            <Toggle on={f.only.rated} onChange={only("rated")}>Rated it</Toggle>
            <Toggle on={f.only.negative} onChange={only("negative")}>Rated badly</Toggle>
            <Toggle on={f.only.saved} onChange={only("saved")}>Saved records</Toggle>
          </div>
        </Field>
      </Toolbar>

      <Card flush>
        {loading ? (
          <RowsSkeleton />
        ) : error ? (
          <ErrorState error={error} />
        ) : rows.length ? (
          <Table
            defaultSort={{ key: "runs", dir: "desc" }}
            onRow={(r) => set({ person: r.userId, tab: "conversations" })}
            columns={[
              { key: "userId", label: "Person", sort: (r) => personLabel(r.userId), render: (r) => <Person id={r.userId} /> },
              { key: "accountId", label: "Workspace", sort: (r) => r.accountId, render: (r) => <Workspace id={r.accountId} /> },
              {
                key: "returning",
                label: "Type",
                render: (r) => (
                  <span title={`First request in this period: ${when(r.firstActive)}`}>
                    <Pill tone={r.returning ? "neutral" : "accent"}>{r.returning ? "Returning" : "New"}</Pill>
                  </span>
                ),
              },
              { key: "runs", label: "Requests", align: "right", render: (r) => fmt(r.runs) },
              { key: "successRate", label: "Success", align: "right", render: (r) => <Rate value={r.successRate} status={statusOf(r.successRate, { good: 0.9, bad: 0.7 })} /> },
              { key: "failed", label: "Failed", align: "right", render: (r) => <span style={{ color: r.failed ? "var(--aa-bad-ink)" : "var(--aa-faint)", fontWeight: r.failed ? 600 : 400 }}>{fmt(r.failed)}</span> },
              { key: "friction", label: "Stuck", align: "right", render: (r) => <span style={{ color: r.friction ? "var(--aa-warn-ink)" : "var(--aa-faint)" }}>{fmt(r.friction)}</span> },
              { key: "ratings", label: "👍 / 👎", align: "right", sort: (r) => r.positive - r.negative, render: (r) => `${r.positive} / ${r.negative}` },
              { key: "recordsSaved", label: "Records", align: "right", render: (r) => fmt(r.recordsSaved) },
              { key: "credits", label: "Credits", align: "right", render: (r) => fmt(r.credits) },
              { key: "lastActive", label: "Last active", nowrap: true, sort: (r) => new Date(r.lastActive).getTime(), render: (r) => <span title={new Date(r.lastActive).toLocaleString()}>{ago(r.lastActive)}</span> },
            ]}
            rows={rows.map((r) => ({ ...r, id: `${r.userId}|${r.accountId}` }))}
          />
        ) : (
          <Empty icon="people" title={all.length ? "No one matches these filters" : "No one used the agent in this period"} />
        )}
      </Card>
    </div>
  );
}
